import React, {ReactNode, type ReactElement} from "react";
import {createPortal} from "react-dom";

import KEY from "/src/constants/keyboard-keydown-key";
import translate from "/src/utils/translate";
import {css, hideWindowScroll, restoreWindowScroll} from "/src/utils/utils";

import Icon from "../icon/Icon";
import CloseIcon from "/src/svg/close.svg";

import * as styles from "./modal.module.scss";

interface ModalProps {
  animationType?: "basic" | "popup";
  onClose: (
    e: React.MouseEvent<HTMLButtonElement> | MouseEvent | KeyboardEvent
  ) => void;
  classes?: string;
  children: ReactElement | ReactNode | string;
  autoWidth?: boolean;
  // element id in modal content
  ariaLabelledBy: string;
  portrait?: boolean;
}

const Modal = (props: ModalProps): ReactElement | null => {
  const {animationType, ariaLabelledBy, children, classes, onClose, portrait} =
    props;
  const closeBtnRef = React.useRef<HTMLButtonElement | null>(null);

  const modalContentRef = React.useRef<HTMLDivElement | null>(null);

  const [mounted, setMounted] = React.useState(false);

  const $portal = React.useRef<HTMLElement | null>(null);

  const handleClose = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement> | MouseEvent | KeyboardEvent) => {
      onClose && onClose(e);

      e && e.stopPropagation();
    },
    [onClose]
  );

  const handleClick = React.useCallback(
    (e: MouseEvent) => {
      if (
        modalContentRef &&
        modalContentRef.current &&
        e.target &&
        !modalContentRef.current.contains(e.target as Node)
      ) {
        handleClose(e);
        e.stopPropagation();
      }
    },
    [handleClose]
  );

  const handleKeyDown = React.useCallback(
    (e: KeyboardEvent) => {
      if (e.key === KEY.ESC) {
        handleClose(e);
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [handleClose]
  );

  React.useEffect(() => {
    // set a little timeout to ensure a repaint and the transitions occur.
    setTimeout(() => setMounted(true), 10);

    // on mount get portal element
    $portal.current = document.getElementById("portal-modal");

    hideWindowScroll();

    // set main content to inert
    const $content = document.getElementById("___gatsby");
    if ($content) {
      $content.inert = true;
    }

    // add keydown listener for modal
    window.addEventListener("keydown", handleKeyDown, false);
    window.addEventListener("click", handleClick, false);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("click", handleClick);
      $portal.current = null;
      restoreWindowScroll();

      if ($content) {
        $content.inert = false;
      }
    };
  }, [handleClick, handleKeyDown]);

  return mounted && $portal.current
    ? createPortal(
        <div
          className={styles.modal}
          role="dialog"
          aria-labelledby={ariaLabelledBy}
          aria-modal="true"
        >
          <div
            ref={modalContentRef}
            className={css(
              styles.modal__content,
              animationType === "popup" ? styles.modal__contentPopup : "",
              portrait ? styles.modal__contentPortrait : "",
              classes!
            )}
          >
            <button
              onClick={handleClose}
              ref={closeBtnRef}
              type="button"
              className={styles.modal__close}
              aria-label={translate("close")}
            >
              <Icon svg={CloseIcon} classes={styles.modal__close__icon} />
            </button>
            {children}
          </div>
        </div>,
        $portal.current
      )
    : null;
};

export default Modal;
